import React, { useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { isMacOs } from 'react-device-detect'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import FormInput from 'components/common/FormInput'
import DazzedParagraph16 from 'components/common/text/DazzedParagraph16'
import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import Tooltip from 'components/common/Tooltip'
import { GRIN_SCOPE_TYPE_LG, GRIN_SCOPE_TYPE_LG_MINI } from 'consts/billingConsts'
import TrademarkText from 'components/common/TrademarkText'

const useStyles = makeStyles({
  inputContainer: {
    alignSelf: 'center'
  },
  grinScopeImageContainer: {
    display: 'flex',
    justifyContent: 'center'
  },
  grinScopeImage: {
    width: 72,
    display: 'block'
  },
  grinScopeImageLG: {
    width: 64
  },
  grinScope: {
    fontWeight: 600
  },
  descriptionNote: {
    marginTop: 4,
    color: 'var(--text-color-3)',
    fontSize: 10,
    lineHeight: '14px'
  },
  scopeTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  tooltipIcon: {
    width: 14,
    height: 14,
    color: 'var(--text-color-3)',
    cursor: 'pointer'
  },
  // Align prices with the quantity inputs while keeping four rows compact.
  amountContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 5,
    marginTop: 4
  },
  discountPriceWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2
  },
  previousPrice: {
    textDecoration: 'line-through',
    color: 'var(--text-color-11)',
    fontSize: 11,
    lineHeight: '14px',
    whiteSpace: 'nowrap'
  },
  currentPrice: {
    color: '#4CAF50',
    fontWeight: 600,
    fontSize: 11,
    lineHeight: '14px',
    whiteSpace: 'nowrap'
  },
  currencyPrefix: {
    fontFamily: 'Roboto',
    fontSize: 13,
    fontWeight: 400,
    marginTop: isMacOs ? 2 : 0
  },
  input: {
    marginTop: '0 !important',
    height: '31px !important'
  },
  inputWrapper: {
    marginTop: 0,
    maxWidth: 130,
    display: 'flex',
    '& input': {
      textAlign: 'center'
    }
  },
  controls: {
    width: 50,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 18,
    fontWeight: 600,
    margin: '0 5px',
    marginBottom: 5,
    cursor: 'pointer',
    userSelect: 'none'
  },
  prompt: {
    '&&': {
      color: 'inherit'
    },
    width: 170,
    minHeight: 20,
    textAlign: 'center',
    fontSize: 10,
    lineHeight: '16px',
    whiteSpace: 'normal'
  },
  promptContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginBottom: 6,
    color: ({ quantityError }) => (quantityError ? 'var(--text-color-17)' : 'var(--text-color-11)')
  },
  disabled: {
    color: 'darkgray',
    cursor: 'auto'
  },
  note: {
    textAlign: 'center!important',
    marginTop: 30,
    color: 'red'
  }
})

const OrderGrinKitsItem = ({
  name,
  description,
  image,
  type,
  price,
  previousPrice,
  hidePrice,
  quantity,
  error,
  descriptionNote,
  helperText,
  productTooltip,
  onSetQuantity,
  onClickMinusQuantity,
  onClickPlusQuantity,
  maxQuantity
}) => {
  const { t } = useTranslation()
  const classes = useStyles({ quantityError: !!error })
  const isLGType = type === GRIN_SCOPE_TYPE_LG || type === GRIN_SCOPE_TYPE_LG_MINI
  const imageClassName = useMemo(
    () => [classes.grinScopeImage, isLGType ? classes.grinScopeImageLG : ''].join(' ').trim(),
    [classes.grinScopeImage, classes.grinScopeImageLG, isLGType]
  )
  const handleClickPlusQuantity = useCallback(
    type => {
      onClickPlusQuantity(type)
    },
    [onClickPlusQuantity]
  )

  const hasReachedOrderLimit = useMemo(() => quantity === maxQuantity, [maxQuantity, quantity])
  const shouldShowPrice = useMemo(
    () => !hidePrice && !error && quantity > 0 && Number(price) > 0,
    [error, hidePrice, price, quantity]
  )
  const shouldShowDiscountPrice = useMemo(
    () => shouldShowPrice && Number(previousPrice) > Number(price),
    [previousPrice, price, shouldShowPrice]
  )
  const promptMessage = useMemo(() => {
    const orderMaxLimitText = hasReachedOrderLimit && t('dialogs.orderScopes.maxSubscriptionQuantityError')
    return error || orderMaxLimitText || helperText || t('dialogs.orderScopes.quantityMultiplyError')
  }, [error, hasReachedOrderLimit, helperText, t])

  return (
    <>
      <Grid container alignItems="center">
        <Grid item xs={2} className={classes.grinScopeImageContainer}>
          <img className={imageClassName} src={image} alt={name} />
        </Grid>
        <Grid item xs={5}>
          <DazzedParagraph16 className={[classes.grinScope, classes.scopeTitle].join(' ')}>
            <TrademarkText text={name} />
            {productTooltip || description ? (
              <Tooltip value={productTooltip || description} placement="top" arrow variant="light">
                <InfoOutlinedIcon className={classes.tooltipIcon} />
              </Tooltip>
            ) : null}
          </DazzedParagraph16>
          {descriptionNote ? (
            <DazzedParagraph12 className={classes.descriptionNote}>{descriptionNote}</DazzedParagraph12>
          ) : null}
        </Grid>
        <Grid item xs={5}>
          <Grid container alignItems="flex-start" justifyContent="center" spacing={1}>
            <Grid item xs={hidePrice ? 12 : 8}>
              <Grid container direction="column" justifyContent="center" alignItems="center">
                <Grid item className={classes.inputWrapper}>
                  <span className={classes.controls} onClick={() => onClickMinusQuantity(type)}>
                    -
                  </span>
                  <FormInput
                    style={{ bright: true, thick: true }}
                    value={quantity}
                    keepErrorContainerWhenInactive={false}
                    inputWrapperClassName={classes.input}
                    containerClassName={classes.inputContainer}
                    setValue={value => {
                      const newValue = !isNaN(value) ? +value : quantity
                      onSetQuantity(+newValue)
                    }}
                  />
                  <span
                    className={[classes.controls, hasReachedOrderLimit && classes.disabled].join(' ')}
                    onClick={() => handleClickPlusQuantity(type)}
                  >
                    +
                  </span>
                </Grid>
                <Grid item>
                  <div className={classes.promptContainer}>
                    <DazzedParagraph12 className={classes.prompt}>{promptMessage}</DazzedParagraph12>
                  </div>
                </Grid>
              </Grid>
            </Grid>
            {!hidePrice && (
              <Grid item xs={4} className={classes.amountContainer}>
                {shouldShowDiscountPrice ? (
                  <div className={classes.discountPriceWrapper}>
                    <DazzedParagraph12 className={classes.previousPrice}>
                      {`$${t('dialogs.orderScopes.scopesAmountPrice', { price: quantity * previousPrice })}`}
                    </DazzedParagraph12>
                    <DazzedParagraph12 className={classes.currentPrice}>
                      {`$${t('dialogs.orderScopes.scopesAmountPrice', { price: quantity * price })}`}
                    </DazzedParagraph12>
                  </div>
                ) : shouldShowPrice ? (
                  <DazzedParagraph12>{`$${t('dialogs.orderScopes.scopesAmountPrice', {
                    price: quantity * price
                  })}`}</DazzedParagraph12>
                ) : null}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default OrderGrinKitsItem
